import { getCreatorCollaborationPackage } from '@/biz/multiPlatformCollaboration/model';
import {
  getCreatorInitialPreOfferResponse,
  type CreatorInitialPreOfferResponse
} from '@/biz/multiPlatformCollaboration/initialPreOffer';
import { isMobile } from '@/biz/tool';
import CreatorPackageQuoteSession from '@/components/multiPlatformCollaboration/CreatorPackageQuoteSession';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useIsMobileMd } from '@/hooks/useIsMobileMd';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useQuoteCompletionVisit } from '@/hooks/useQuoteCompletionVisit';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { OrderStatus } from '@/interface/order';
import { Drawer } from 'aha-components';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BottomDrawer from '../BottomDrawer';

// PC端 接单总弹窗

const ModalShowAnInterest: React.FC = () => {
  const { t } = useTranslation();
  const isMobileViewport = useIsMobileMd();
  const isMobileLayout = isMobile() || isMobileViewport;
  const { showModalTakeOrder, setShowModalTakeOrder, initialPreOfferResponse } =
    useWebModalStore();
  const { setScreeningQuestions } = useMultiplePricesStore();
  const { detailData } = useDetailDataStore();
  const collaborationPackage = useMemo(
    () => getCreatorCollaborationPackage(detailData?.order),
    [detailData?.order]
  );
  // Home/deep-link entries have no explicit action. A valid brand offer starts
  // in acceptance mode, not an editable quote that bypasses fixed-price rules.
  // Capture once per opening; detail refreshes must not silently accept a new price.
  const defaultResponseRef = useRef<{
    orderId: string;
    response: CreatorInitialPreOfferResponse | null;
  } | null>(null);
  if (!showModalTakeOrder) defaultResponseRef.current = null;
  else if (
    collaborationPackage &&
    defaultResponseRef.current?.orderId !== collaborationPackage.id
  ) {
    defaultResponseRef.current = {
      orderId: collaborationPackage.id,
      response: getCreatorInitialPreOfferResponse(
        collaborationPackage,
        useMultiplePricesStore.getState().creatorInitialContentTypeSelection,
        'accept'
      )
    };
  }
  const preOfferResponse =
    initialPreOfferResponse ?? defaultResponseRef.current?.response;
  const isSingleChannelOrder =
    collaborationPackage?.placements.filter(
      (placement) => placement.raw?.active !== false
    ).length === 1;
  const completionVisit = useQuoteCompletionVisit(collaborationPackage?.id);
  // 弹窗刚打开时需要重置 answer；detail 刷新时要保留已填内容
  const resetScreeningAnswersOnSyncRef = useRef(false);

  // 关闭弹窗时，清空状态；打开时标记下次同步需要重置答案
  useEffect(() => {
    if (!showModalTakeOrder) {
      const store = useMultiplePricesStore.getState();
      store.resetCreatorScheduleConflict();
      store.setCompetitivePriceDetail(null);
      store.setNewOrderPrice('');
      store.setScreeningQuestions([]);
      store.setScreeningQuestionsError(false);
      resetScreeningAnswersOnSyncRef.current = false;
      return;
    }

    resetScreeningAnswersOnSyncRef.current = true;
  }, [showModalTakeOrder]);

  // 同步 screening questions：
  // - 刚打开弹窗：用空 answer 初始化
  // - 打开后 questions 晚到：补初始化
  // - detail 刷新：保留已填 answer，避免被清空
  useEffect(() => {
    if (!showModalTakeOrder) return;

    const campaignScreeningQuestions = detailData?.campaign?.screeningQuestions;
    if (!campaignScreeningQuestions?.length) {
      // campaign 已加载且确实没有题目时才清空；detail 未就绪时不要动
      if (detailData?.campaign) {
        setScreeningQuestions([]);
        resetScreeningAnswersOnSyncRef.current = false;
      }
      return;
    }

    const existing = useMultiplePricesStore.getState().screeningQuestions;
    const shouldResetAnswers = resetScreeningAnswersOnSyncRef.current;
    resetScreeningAnswersOnSyncRef.current = false;

    setScreeningQuestions(
      campaignScreeningQuestions.map((question: any, index: number) => ({
        ...question,
        answer: shouldResetAnswers
          ? (question?.answer ?? '')
          : (existing[index]?.answer ?? question?.answer ?? '')
      }))
    );
  }, [
    showModalTakeOrder,
    detailData?.campaign,
    detailData?.campaign?.screeningQuestions,
    setScreeningQuestions
  ]);

  const handleClose = () => {
    setShowModalTakeOrder(false);
  };

  if (
    !collaborationPackage ||
    detailData?.order?.status !== OrderStatus.Invited
  ) {
    return null;
  }

  return (
    <>
      {isMobileLayout ? (
        <BottomDrawer
          title={
            <div className="text-2xl font-semibold leading-8">
              {t('multi_platform.quote.submit_interest', {
                defaultValue: 'Submit interest'
              })}
            </div>
          }
          visible={showModalTakeOrder}
          handleClose={handleClose}
          rootClassName="!z-[10000]"
          headerClassName="!h-8 !min-h-8"
          titleClassName="!mb-0"
          closeClassName="flex h-8 w-8 flex-none cursor-pointer items-center justify-center [&_svg]:h-5 [&_svg]:w-5"
          isShowFooter={false}
          contentClassName="[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          className="!h-[calc(100dvh-env(safe-area-inset-top,0px))] !max-h-[calc(100dvh-env(safe-area-inset-top,0px))] !rounded-t-xl !p-[16px]"
        >
          {showModalTakeOrder ? (
            <CreatorPackageQuoteSession
              key={`${collaborationPackage.id}:${preOfferResponse?.mode ?? 'quote'}`}
              collaborationPackage={collaborationPackage}
              initialPreOfferResponse={preOfferResponse}
              completionVisit={completionVisit}
              initialAudienceViews={detailData?.audienceViews}
              mobile
              onClose={handleClose}
            />
          ) : null}
        </BottomDrawer>
      ) : (
        <Drawer
          width="min(1200px, 100vw)"
          title={
            <div className="text-2xl font-semibold leading-9 tracking-[-0.24px]">
              {isSingleChannelOrder
                ? t('multi_platform.quote.submit_interest', {
                    defaultValue: 'Submit interest'
                  })
                : t('multi_platform.quote.share_title', {
                    defaultValue: 'Share your package rate'
                  })}
            </div>
          }
          open={showModalTakeOrder}
          onClose={handleClose}
          bodyStyle={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            padding: '0px'
          }}
          headerStyle={{ height: 68, padding: '16px 24px' }}
          placement="right"
        >
          {showModalTakeOrder ? (
            <CreatorPackageQuoteSession
              key={`${collaborationPackage.id}:${preOfferResponse?.mode ?? 'quote'}`}
              collaborationPackage={collaborationPackage}
              initialPreOfferResponse={preOfferResponse}
              completionVisit={completionVisit}
              initialAudienceViews={detailData?.audienceViews}
              onClose={handleClose}
            />
          ) : null}
        </Drawer>
      )}
    </>
  );
};

export default ModalShowAnInterest;
